import React, { useState } from 'react';
import { CreditCard, Eye, EyeOff, MapPin, DollarSign } from 'lucide-react';
import { PaymentGatewayConfig } from '../PlatformSettings';

interface PaymentGatewayFormProps {
  settings: PaymentGatewayConfig;
  onUpdate: (updates: Partial<PaymentGatewayConfig>) => void;
}

export const PaymentGatewayForm: React.FC<PaymentGatewayFormProps> = ({ settings, onUpdate }) => {
  const [showSecrets, setShowSecrets] = useState<{ [key: string]: boolean }>({});

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      let updates: any = {};
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        updates = {
          [parent]: {
            ...settings[parent as keyof PaymentGatewayConfig],
            [child]: value,
          },
        };
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        updates = {
          [parent]: {
            ...settings[parent as keyof PaymentGatewayConfig],
            [child]: {
              ...(settings[parent as keyof PaymentGatewayConfig] as any)?.[child],
              [grandchild]: value,
            },
          },
        };
      }
      
      onUpdate(updates);
    } else {
      onUpdate({ [field]: value });
    }
  };

  const toggleSecretVisibility = (field: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const renderSecretInput = (
    label: string,
    field: string,
    value: string,
    placeholder: string,
    required: boolean = false
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <input
          type={showSecrets[field] ? 'text' : 'password'}
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="button"
          onClick={() => toggleSecretVisibility(field)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {showSecrets[field] ? (
            <EyeOff className="h-4 w-4 text-gray-400" />
          ) : (
            <Eye className="h-4 w-4 text-gray-400" />
          )}
        </button>
      </div>
    </div>
  );

  const canadianProvinces = [
    { code: 'AB', name: 'Alberta', pst: 0 },
    { code: 'BC', name: 'British Columbia', pst: 0.07 },
    { code: 'MB', name: 'Manitoba', pst: 0.07 },
    { code: 'NB', name: 'New Brunswick', hst: 0.15 },
    { code: 'NL', name: 'Newfoundland and Labrador', hst: 0.15 },
    { code: 'NS', name: 'Nova Scotia', hst: 0.15 },
    { code: 'ON', name: 'Ontario', hst: 0.13 },
    { code: 'PE', name: 'Prince Edward Island', hst: 0.15 },
    { code: 'QC', name: 'Quebec', pst: 0.09975 },
    { code: 'SK', name: 'Saskatchewan', pst: 0.06 },
    { code: 'NT', name: 'Northwest Territories', pst: 0 },
    { code: 'NU', name: 'Nunavut', pst: 0 },
    { code: 'YT', name: 'Yukon', pst: 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <CreditCard className="w-6 h-6 text-blue-500 mr-3" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Payment Gateway Configuration</h3>
          <p className="text-sm text-gray-600">Configure payment processing for Canadian businesses</p>
        </div>
      </div>

      {/* Enable Payment Gateway */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="payment-enabled"
          checked={settings.enabled}
          onChange={(e) => handleInputChange('enabled', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="payment-enabled" className="ml-2 block text-sm text-gray-900">
          Enable Payment Gateway
        </label>
      </div>

      {settings.enabled && (
        <>
          {/* Provider and Mode */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Provider *
              </label>
              <select
                value={settings.provider}
                onChange={(e) => handleInputChange('provider', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="moneris">Moneris (Canada)</option>
                <option value="bambora">Bambora (Canada)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mode *
              </label>
              <select
                value={settings.mode}
                onChange={(e) => handleInputChange('mode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="test">Test/Sandbox</option>
                <option value="live">Live/Production</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency *
              </label>
              <select
                value={settings.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="USD">USD - US Dollar</option>
              </select>
            </div>
          </div>

          {/* Provider-specific Configuration */}
          {settings.provider === 'stripe' && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-4 h-4 mr-2" />
                Stripe Configuration
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publishable Key *
                  </label>
                  <input
                    type="text"
                    value={settings.stripe?.publishableKey || ''}
                    onChange={(e) => handleInputChange('stripe.publishableKey', e.target.value)}
                    placeholder="pk_test_..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {renderSecretInput(
                  'Secret Key',
                  'stripe.secretKey',
                  settings.stripe?.secretKey || '',
                  'sk_test_...',
                  true
                )}
                {renderSecretInput(
                  'Webhook Secret',
                  'stripe.webhookSecret',
                  settings.stripe?.webhookSecret || '',
                  'whsec_...'
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={settings.stripe?.accountId || ''}
                    onChange={(e) => handleInputChange('stripe.accountId', e.target.value)}
                    placeholder="acct_..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {settings.provider === 'paypal' && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-4">PayPal Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID *
                  </label>
                  <input
                    type="text"
                    value={settings.paypal?.clientId || ''}
                    onChange={(e) => handleInputChange('paypal.clientId', e.target.value)}
                    placeholder="PayPal Client ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {renderSecretInput(
                  'Client Secret',
                  'paypal.clientSecret',
                  settings.paypal?.clientSecret || '',
                  'PayPal Client Secret',
                  true
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook ID
                  </label>
                  <input
                    type="text"
                    value={settings.paypal?.webhookId || ''}
                    onChange={(e) => handleInputChange('paypal.webhookId', e.target.value)}
                    placeholder="PayPal Webhook ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {settings.provider === 'moneris' && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <MapPin className="w-4 h-4 mr-2" />
                Moneris Configuration (Canada)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Store ID *
                  </label>
                  <input
                    type="text"
                    value={settings.moneris?.storeId || ''}
                    onChange={(e) => handleInputChange('moneris.storeId', e.target.value)}
                    placeholder="Moneris Store ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {renderSecretInput(
                  'API Token',
                  'moneris.apiToken',
                  settings.moneris?.apiToken || '',
                  'Moneris API Token',
                  true
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Environment *
                  </label>
                  <select
                    value={settings.moneris?.environment || 'qa'}
                    onChange={(e) => handleInputChange('moneris.environment', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="qa">QA/Testing</option>
                    <option value="prod">Production</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {settings.provider === 'bambora' && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <MapPin className="w-4 h-4 mr-2" />
                Bambora Configuration (Canada)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Merchant ID *
                  </label>
                  <input
                    type="text"
                    value={settings.bambora?.merchantId || ''}
                    onChange={(e) => handleInputChange('bambora.merchantId', e.target.value)}
                    placeholder="Bambora Merchant ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {renderSecretInput(
                  'API Key',
                  'bambora.apiKey',
                  settings.bambora?.apiKey || '',
                  'Bambora API Key',
                  true
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Environment *
                  </label>
                  <select
                    value={settings.bambora?.environment || 'sandbox'}
                    onChange={(e) => handleInputChange('bambora.environment', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Production</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Canadian Tax Configuration */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-4 h-4 mr-2" />
              Canadian Tax Configuration
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={(settings.taxConfig.gstRate * 100).toFixed(2)}
                  onChange={(e) => handleInputChange('taxConfig.gstRate', parseFloat(e.target.value) / 100)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HST Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={(settings.taxConfig.hstRate * 100).toFixed(2)}
                  onChange={(e) => handleInputChange('taxConfig.hstRate', parseFloat(e.target.value) / 100)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HST Provinces
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {canadianProvinces.filter(p => p.hst).map(province => (
                  <label key={province.code} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.taxConfig.hstProvinces.includes(province.code)}
                      onChange={(e) => {
                        const newProvinces = e.target.checked
                          ? [...settings.taxConfig.hstProvinces, province.code]
                          : settings.taxConfig.hstProvinces.filter(p => p !== province.code);
                        handleInputChange('taxConfig.hstProvinces', newProvinces);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    />
                    <span className="text-sm">{province.code}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provincial Sales Tax (PST) Rates
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {canadianProvinces.filter(p => p.pst > 0).map(province => (
                  <div key={province.code} className="flex items-center space-x-2">
                    <span className="text-sm font-medium w-8">{province.code}:</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={((settings.taxConfig.pstRates[province.code] || 0) * 100).toFixed(2)}
                      onChange={(e) => {
                        const newRates = {
                          ...settings.taxConfig.pstRates,
                          [province.code]: parseFloat(e.target.value) / 100
                        };
                        handleInputChange('taxConfig.pstRates', newRates);
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">Payment Gateway Tips:</h5>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Always test your configuration in sandbox/test mode first</li>
              <li>• Moneris and Bambora are popular Canadian payment processors</li>
              <li>• Tax rates are automatically applied based on customer location</li>
              <li>• Keep your API keys secure and rotate them regularly</li>
              <li>• Configure webhooks for real-time payment notifications</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
